// /admin/leads-frios — leads frios (prospecção) em listas com colunas
// personalizadas. Query params:
//   ?list=<uuid>   lista ativa (padrão: a mais recente)
//   ?status=new|researching|qualified|promoted|discarded
//   ?q=<texto>     busca (nome, contato, cidade, telefone, e-mail)
//   ?page=<n>      página (50 por página)
// Regras: docs/specs/flows/prospeccao.md.
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import { EyeOff, MoreVertical, Plus, Search, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { DataTable, DataTablePagination } from "@/components/shared/DataTable";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadErrorState } from "@/components/shared/LoadErrorState";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import {
  useBulkProspectStatus,
  useCreateProspect,
  useCreateProspectField,
  useDeleteProspectField,
  useDeleteProspects,
  useProspectFields,
  useProspectLists,
  useProspects,
  useUpdateProspect,
  useUpdateProspectField,
  type Prospect,
  type ProspectField,
} from "@/hooks/useProspects";
import { PROSPECT_STATUSES, type ProspectStatus } from "@/lib/prospectFields";
import {
  EDITABLE_PROSPECT_STATUSES,
  FIELD_TYPE_META,
  PROSPECT_STATUS_META,
  PROSPECT_STATUS_ORDER,
} from "@/lib/prospectDisplay";
import { cn } from "@/lib/utils";
import { FieldDialog } from "./FieldDialog";
import { ImportProspectsDialog } from "./ImportProspectsDialog";
import { PromoteProspectDialog } from "./PromoteProspectDialog";
import { ProspectCell } from "./ProspectCell";
import { ProspectListNav } from "./ProspectListNav";
import { ProspectSheet } from "./ProspectSheet";
import { CORE_CELL_FIELDS, type CoreCellKey } from "./prospectColumns";

const PAGE_SIZE = 50;
const GRID_CORE: CoreCellKey[] = ["phone", "email", "city", "state", "website", "instagram"];

function isStatus(v: string | null): v is ProspectStatus {
  return !!v && (PROSPECT_STATUSES as readonly string[]).includes(v);
}

export function ProspectsPage() {
  const [params, setParams] = useSearchParams();
  const {
    data: lists = [],
    isLoading: listsLoading,
    isError: listsError,
    refetch,
  } = useProspectLists();
  const listId = params.get("list") ?? lists[0]?.id ?? null;
  const list = lists.find((l) => l.id === listId) ?? null;
  const status = isStatus(params.get("status")) ? (params.get("status") as ProspectStatus) : null;
  const page = Math.max(1, Number(params.get("page")) || 1);
  const [search, setSearch] = useState(params.get("q") ?? "");

  const setParam = (key: string, value: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        if (key !== "page") next.delete("page");
        return next;
      },
      { replace: true },
    );

  // Busca com debounce para não consultar a cada tecla.
  useEffect(() => {
    const t = setTimeout(() => {
      if ((params.get("q") ?? "") !== search) setParam("q", search.trim() || null);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const fieldsQuery = useProspectFields(listId);
  const fields = useMemo(() => fieldsQuery.data ?? [], [fieldsQuery.data]);
  const prospectsQuery = useProspects({
    listId,
    status,
    search: params.get("q") ?? "",
    page,
    pageSize: PAGE_SIZE,
  });
  const rows = useMemo(() => prospectsQuery.data?.rows ?? [], [prospectsQuery.data]);
  const total = prospectsQuery.data?.total ?? 0;

  const [selection, setSelection] = useState<RowSelectionState>({});
  const [openId, setOpenId] = useState<string | null>(null);
  const [promoteIds, setPromoteIds] = useState<string[] | null>(null);
  const [fieldDialog, setFieldDialog] = useState<{ field?: ProspectField } | null>(null);
  const [fieldToDelete, setFieldToDelete] = useState<ProspectField | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => setSelection({}), [listId, status, page]);

  const updateProspect = useUpdateProspect();
  const createProspect = useCreateProspect();
  const bulkStatus = useBulkProspectStatus();
  const deleteProspects = useDeleteProspects();
  const createField = useCreateProspectField();
  const updateField = useUpdateProspectField();
  const deleteField = useDeleteProspectField();

  const selectedIds = Object.keys(selection).filter((id) => selection[id]);
  const opened = rows.find((r) => r.id === openId) ?? null;
  const promoteRows = rows.filter((r) => promoteIds?.includes(r.id));

  const saveCore = (id: string, key: CoreCellKey, value: unknown) => {
    if (key === "name" && !value) return;
    updateProspect.mutate({ id, core: { [key]: value as string | null } });
  };
  const saveData = (id: string, key: string, value: unknown) =>
    updateProspect.mutate({ id, data: { [key]: value } });

  const columns = useMemo<ColumnDef<Prospect, unknown>[]>(() => {
    const cols: ColumnDef<Prospect, unknown>[] = [
      {
        id: "name",
        accessorKey: "name",
        header: "Nome",
        meta: { minWidth: 240 },
        cell: ({ row }) => (
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setOpenId(row.original.id)}
              className="min-w-0 truncate text-left font-semibold hover:text-primary hover:underline"
            >
              {row.original.name}
            </button>
            {row.original.lead_id && (
              <Link to={`/admin/leads/${row.original.lead_id}`} className="shrink-0">
                <StatusBadge shape="tag" tone="ok">
                  No funil
                </StatusBadge>
              </Link>
            )}
          </div>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => {
          const meta = PROSPECT_STATUS_META[row.original.status];
          if (row.original.status === "promoted") {
            return (
              <StatusBadge shape="tag" tone={meta.tone}>
                {meta.label}
              </StatusBadge>
            );
          }
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" aria-label={`Status: ${meta.label} — alterar`}>
                  <StatusBadge shape="tag" tone={meta.tone}>
                    {meta.label}
                  </StatusBadge>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {EDITABLE_PROSPECT_STATUSES.map((s) => (
                  <DropdownMenuItem
                    key={s}
                    onSelect={() =>
                      updateProspect.mutate({ id: row.original.id, core: { status: s } })
                    }
                  >
                    {PROSPECT_STATUS_META[s].label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
      ...GRID_CORE.map((key): ColumnDef<Prospect, unknown> => {
        const def = CORE_CELL_FIELDS.find((f) => f.key === key)!;
        return {
          id: key,
          accessorFn: (r) => r[key] ?? "",
          header: def.label,
          meta: { minWidth: key === "state" ? 70 : 150 },
          cell: ({ row }) => (
            <ProspectCell
              field={def}
              value={row.original[key]}
              onSave={(v) => saveCore(row.original.id, key, v)}
            />
          ),
        };
      }),
      ...fields
        .filter((f) => !f.hidden)
        .map((f): ColumnDef<Prospect, unknown> => ({
          id: `data.${f.key}`,
          accessorFn: (r) => {
            const v = r.data[f.key];
            return typeof v === "number" || typeof v === "string" ? v : v == null ? "" : String(v);
          },
          meta: {
            minWidth: f.type === "long_text" ? 260 : 150,
            headerAction: (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label={`Opções da coluna ${f.label}`}
                    className="rounded p-0.5 hover:bg-background"
                  >
                    <MoreVertical className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onSelect={() => setFieldDialog({ field: f })}>
                    Editar coluna…
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() =>
                      updateField.mutate({ id: f.id, listId: f.list_id, patch: { hidden: true } })
                    }
                  >
                    Ocultar
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-bad" onSelect={() => setFieldToDelete(f)}>
                    Excluir coluna…
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ),
          },
          header: () => <span title={FIELD_TYPE_META[f.type].label}>{f.label}</span>,
          cell: ({ row }) => (
            <ProspectCell
              field={f}
              value={row.original.data[f.key]}
              onSave={(v) => saveData(row.original.id, f.key, v)}
            />
          ),
        })),
      {
        id: "__add",
        enableSorting: false,
        meta: { label: "Adicionar coluna" },
        header: () => (
          <button
            type="button"
            aria-label="Adicionar coluna"
            onClick={() => setFieldDialog({})}
            className="grid h-7 w-7 place-items-center rounded-md border border-dashed border-border bg-card hover:bg-muted"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        ),
        cell: () => null,
      },
    ];
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  const hiddenFields = fields.filter((f) => f.hidden);

  if (listsError) return <LoadErrorState onRetry={() => refetch()} />;

  return (
    <div className="mx-auto flex max-w-[1440px] flex-wrap gap-6">
      <ProspectListNav
        lists={lists}
        activeId={listId}
        onSelect={(id) => {
          setParams(new URLSearchParams({ list: id }), { replace: true });
          setSearch("");
        }}
        className="w-full shrink-0 md:w-[230px]"
      />

      <div className="flex min-w-0 flex-[999_1_640px] flex-col gap-[18px]">
        <PageHeader
          title={list?.name ?? "Leads frios"}
          description={
            list
              ? `${list.total} leads frios · ${list.promoted} no funil${list.source ? ` · fonte: ${list.source}` : ""}`
              : "Contatos de prospecção que ainda não estão no funil."
          }
          actions={
            list && (
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={() => setImportOpen(true)}>
                  <Upload className="mr-1.5 h-4 w-4" aria-hidden="true" /> Importar
                </Button>
                <Button type="button" variant="outline" onClick={() => setFieldDialog({})}>
                  <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" /> Coluna
                </Button>
                <Button
                  type="button"
                  disabled={createProspect.isPending}
                  onClick={async () => {
                    const created = await createProspect.mutateAsync({
                      listId: list.id,
                      core: { name: "Novo lead frio" },
                    });
                    setOpenId(created.id);
                  }}
                >
                  <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" /> Lead frio
                </Button>
              </div>
            )
          }
        />

        {!listsLoading && lists.length === 0 ? (
          <EmptyState
            title="Nenhuma lista ainda"
            description="Crie uma lista em “Nova lista” e importe uma planilha, ou peça ao Claude (MCP import_prospects)."
          />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2.5">
              <div className="relative">
                <Search
                  className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                  aria-hidden="true"
                />
                <Input
                  aria-label="Buscar leads frios"
                  placeholder="Buscar por nome, cidade, telefone…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-9 w-[280px] max-w-full rounded-[9px] bg-card pl-8 text-[13px]"
                />
              </div>
              <div role="group" aria-label="Filtrar por status" className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  aria-pressed={!status}
                  onClick={() => setParam("status", null)}
                  className={cn(
                    "h-8 rounded-full border px-3 text-[12.5px] font-semibold",
                    !status
                      ? "border-foreground bg-foreground text-background"
                      : "border-border bg-card",
                  )}
                >
                  Todos
                </button>
                {PROSPECT_STATUS_ORDER.map((s) => (
                  <button
                    key={s}
                    type="button"
                    aria-pressed={status === s}
                    onClick={() => setParam("status", status === s ? null : s)}
                    className={cn(
                      "h-8 rounded-full border px-3 text-[12.5px] font-semibold",
                      status === s
                        ? "border-foreground bg-foreground text-background"
                        : "border-border bg-card",
                    )}
                  >
                    {PROSPECT_STATUS_META[s].label}
                  </button>
                ))}
              </div>
              {hiddenFields.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground"
                    >
                      <EyeOff className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                      {hiddenFields.length} ocultas
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {hiddenFields.map((f) => (
                      <DropdownMenuItem
                        key={f.id}
                        onSelect={() =>
                          updateField.mutate({
                            id: f.id,
                            listId: f.list_id,
                            patch: { hidden: false },
                          })
                        }
                      >
                        Mostrar “{f.label}”
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {selectedIds.length > 0 && (
              <div
                role="region"
                aria-label="Ações em massa"
                className="flex flex-wrap items-center gap-2.5 rounded-xl bg-panel px-3.5 py-2.5 text-[13px] text-panel-foreground"
              >
                <span className="font-bold">{selectedIds.length} selecionados</span>
                <Button type="button" size="sm" onClick={() => setPromoteIds(selectedIds)}>
                  Mover para o funil
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" size="sm" variant="secondary">
                      Mudar status
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {EDITABLE_PROSPECT_STATUSES.map((s) => (
                      <DropdownMenuItem
                        key={s}
                        onSelect={() =>
                          bulkStatus.mutate(
                            { ids: selectedIds, status: s },
                            { onSuccess: () => setSelection({}) },
                          )
                        }
                      >
                        {PROSPECT_STATUS_META[s].label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  onClick={() => setConfirmDelete(true)}
                >
                  Excluir
                </Button>
                <button
                  type="button"
                  className="ml-auto text-[12.5px] opacity-80 hover:opacity-100"
                  onClick={() => setSelection({})}
                >
                  Limpar seleção
                </button>
              </div>
            )}

            <DataTable
              ariaLabel={`Leads frios — ${list?.name ?? ""}`}
              columns={columns}
              data={rows}
              getRowId={(r) => r.id}
              isLoading={prospectsQuery.isLoading || fieldsQuery.isLoading || listsLoading}
              error={
                prospectsQuery.isError ? (
                  <LoadErrorState size="sm" onRetry={() => prospectsQuery.refetch()} />
                ) : undefined
              }
              empty={
                <EmptyState
                  size="sm"
                  title={
                    params.get("q") || status
                      ? "Nada encontrado com esses filtros."
                      : "Lista vazia."
                  }
                  description={
                    params.get("q") || status
                      ? undefined
                      : "Use “Importar” para trazer uma planilha."
                  }
                />
              }
              enableRowSelection
              rowSelection={selection}
              onRowSelectionChange={setSelection}
              stickyFirstColumn
              minWidth={1100}
              footer={
                total > 0 ? (
                  <DataTablePagination
                    page={page}
                    pageSize={PAGE_SIZE}
                    total={total}
                    itemLabel="leads frios"
                    onPageChange={(p) => setParam("page", String(p))}
                  />
                ) : undefined
              }
            />
          </>
        )}
      </div>

      {list && (
        <>
          <ProspectSheet
            prospect={opened}
            listName={list.name}
            fields={fields}
            onOpenChange={(o) => !o && setOpenId(null)}
            onSaveCore={(key, v) => opened && saveCore(opened.id, key, v)}
            onSaveData={(key, v) => opened && saveData(opened.id, key, v)}
            onPromote={() => opened && setPromoteIds([opened.id])}
          />
          <ImportProspectsDialog
            open={importOpen}
            onOpenChange={setImportOpen}
            listId={list.id}
            listName={list.name}
            fields={fields}
          />
          <FieldDialog
            open={!!fieldDialog}
            onOpenChange={(o) => !o && setFieldDialog(null)}
            initial={fieldDialog?.field}
            isPending={createField.isPending || updateField.isPending}
            onSubmit={async (value) => {
              if (fieldDialog?.field) {
                await updateField.mutateAsync({
                  id: fieldDialog.field.id,
                  listId: list.id,
                  patch: value,
                });
              } else {
                await createField.mutateAsync({ listId: list.id, existing: fields, ...value });
              }
              setFieldDialog(null);
            }}
          />
        </>
      )}

      <PromoteProspectDialog
        prospects={promoteRows}
        open={!!promoteIds && promoteRows.length > 0}
        onOpenChange={(o) => !o && setPromoteIds(null)}
        onDone={() => setSelection({})}
      />

      <AlertDialog open={!!fieldToDelete} onOpenChange={(o) => !o && setFieldToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir coluna “{fieldToDelete?.label}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Os valores dessa coluna em todos os leads frios da lista também são apagados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-bad text-bad-foreground hover:bg-bad/90"
              onClick={() =>
                fieldToDelete &&
                deleteField.mutate({ id: fieldToDelete.id, listId: fieldToDelete.list_id })
              }
            >
              Excluir coluna
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir {selectedIds.length} leads frios?</AlertDialogTitle>
            <AlertDialogDescription>
              Leads já movidos para o funil continuam no funil. Não dá para desfazer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-bad text-bad-foreground hover:bg-bad/90"
              onClick={() =>
                deleteProspects.mutate(selectedIds, { onSuccess: () => setSelection({}) })
              }
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
